/**
 * The small marks that repeat across every list and detail view.
 *
 * `BelegNumber` is the app's signature: the gapless receipt number in the mono face, the thing an
 * Austrian bookkeeper actually navigates by. `SourceMark` shows where an extracted value came
 * from, so nobody has to trust a number without knowing whether it was read off a structured
 * e-invoice or guessed from blurry OCR.
 */
import { useTranslation } from "react-i18next";
import type { DocumentKind, DocumentStatus } from "@/lib/api/documents";
import { cn } from "@/lib/utils/cn";

export function BelegNumber({ value, className }: { value?: string; className?: string }) {
  if (!value) return null;
  return (
    <span
      className={cn(
        // An identifier: it never breaks across lines, the column makes room for it instead.
        "rounded border px-1.5 py-0.5 ledger-id whitespace-nowrap text-muted-foreground",
        className,
      )}
      data-testid="beleg-number"
    >
      {value}
    </span>
  );
}

/** ER / AR / KA - the three Belegkreise. Kept as the abbreviation everyone uses on paper. */
export function KindBadge({ kind, className }: { kind: DocumentKind; className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        "inline-flex h-5 items-center rounded-sm border px-1.5 ledger-id font-medium",
        kind === "AR" ? "border-brand/40 text-brand" : "border-border text-muted-foreground",
        className,
      )}
      title={t(`kinds.${kind}`)}
    >
      {kind}
    </span>
  );
}

const STATUS_COLOR: Record<DocumentStatus, string> = {
  inbox: "text-warning",
  review: "text-warning",
  booked: "text-success",
  archived: "text-muted-foreground",
};

/** Status without a pill: the colour lives in a 2px rule on the row, like a tab in a binder. */
export function statusRule(status: DocumentStatus): string {
  return cn("tab-rule", STATUS_COLOR[status]);
}

export function StatusLabel({ status }: { status: DocumentStatus }) {
  const { t } = useTranslation();
  return (
    <span className={cn("text-xs font-medium whitespace-nowrap", STATUS_COLOR[status])}>
      {t(`status.${status}`)}
    </span>
  );
}

export type ExtractionSource = "einvoice" | "qr" | "text" | "llm";

export function SourceMark({ source }: { source?: string }) {
  const { t } = useTranslation();
  if (!source) return null;
  const known = ["einvoice", "qr", "text", "llm"].includes(source)
    ? (source as ExtractionSource)
    : null;
  if (!known) return null;
  return (
    <span
      className={cn(
        "rounded-sm px-1 py-px ledger-id",
        // Exact sources are ledger green, the model's guesses violet, the text layer plain.
        known === "einvoice" || known === "qr"
          ? "bg-success/10 text-success"
          : known === "llm"
            ? "bg-model/10 text-model"
            : "bg-muted text-muted-foreground",
      )}
      title={t(`extraction.sourceHint.${known}`)}
      data-testid={`source-${known}`}
    >
      {t(`extraction.source.${known}`)}
    </span>
  );
}
